import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { 
  BarChart3, Eye, Users, TrendingUp, Globe, Smartphone, Monitor, Tablet,
  RefreshCw, ArrowLeft, Calendar, Clock, MousePointerClick, Share2,
  Chrome, Laptop, MapPin, Bike, BedDouble, ArrowUpRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAnalytics = () => {
  const { isAdmin, adminPassword, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/admin/login');
      return;
    }
    if (isAdmin) {
      fetchAnalytics();
    }
  }, [isAdmin, authLoading, navigate, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/dashboard?days=${period}`, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isAdmin) return null;

  const summary = analytics?.summary || { total_pageviews: 0, unique_visitors: 0, total_registrations: 0 };
  const conversionRate = summary.total_pageviews > 0 
    ? ((summary.total_registrations / summary.total_pageviews) * 100).toFixed(2) 
    : 0;

  // Process device data
  const deviceData = analytics?.devices || [];
  const totalDeviceViews = deviceData.reduce((sum, d) => sum + d.count, 0);
  const getDevicePercent = (device) => {
    const item = deviceData.find(d => d.device?.toLowerCase() === device.toLowerCase());
    return item ? Math.round((item.count / totalDeviceViews) * 100) : 0;
  };

  // Process traffic sources
  const trafficSources = analytics?.traffic_sources || [];
  
  // Process countries
  const countries = analytics?.countries || [];
  
  // Process browsers
  const browsers = analytics?.browsers || [];
  
  // Process funnel
  const funnelData = analytics?.funnel || [];
  
  // Process daily trend
  const dailyTrend = analytics?.daily_trend || [];
  
  // Process motorcycle choices
  const motoChoices = analytics?.motorcycle_choices || [];
  
  // Process room types
  const roomTypes = analytics?.room_types || [];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/admin/dashboard" className="mr-4 p-2 hover:bg-white/10 rounded-lg transition-colors">
                <ArrowLeft size={24} />
              </Link>
              <div>
                <h1 className="text-3xl font-bold flex items-center">
                  <BarChart3 className="mr-3" size={32} />
                  Analytics Dashboard
                </h1>
                <p className="text-indigo-200 mt-1">Statistiche dettagliate del sito</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={period}
                onChange={(e) => setPeriod(Number(e.target.value))}
                className="bg-white/10 border border-white/20 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50"
              >
                <option value={7} className="text-gray-900">Ultimi 7 giorni</option>
                <option value={30} className="text-gray-900">Ultimi 30 giorni</option>
                <option value={90} className="text-gray-900">Ultimi 90 giorni</option>
              </select>
              <button
                onClick={fetchAnalytics}
                disabled={loading}
                className="flex items-center bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Visualizzazioni Totali</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? '...' : summary.total_pageviews.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Eye className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Visitatori Unici</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? '...' : summary.unique_visitors.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Iscrizioni</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? '...' : summary.total_registrations.toLocaleString()}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <MousePointerClick className="text-purple-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-amber-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Tasso Conversione</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {loading ? '...' : `${conversionRate}%`}
                </p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <TrendingUp className="text-amber-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Daily Trend Chart */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Calendar className="mr-2 text-indigo-600" size={24} />
              Trend Giornaliero
            </h2>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-200 rounded"></div>
            ) : dailyTrend.length > 0 ? (
              <div className="h-48 flex items-end gap-1">
                {dailyTrend.slice(-14).map((day, i) => {
                  const maxCount = Math.max(...dailyTrend.map(d => d.count));
                  const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-indigo-500 rounded-t transition-all hover:bg-indigo-600"
                        style={{ height: `${Math.max(height, 2)}%` }}
                        title={`${day.date}: ${day.count} visite`}
                      ></div>
                      <span className="text-xs text-gray-500 mt-1 rotate-45 origin-left">
                        {day.date.slice(5)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessun dato disponibile</p>
            )}
          </div>

          {/* Device Stats */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Smartphone className="mr-2 text-blue-600" size={24} />
              Dispositivi
            </h2>
            {loading ? (
              <div className="space-y-4 animate-pulse">
                {[1,2,3].map(i => <div key={i} className="h-12 bg-gray-200 rounded"></div>)}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center">
                  <Monitor className="text-gray-600 mr-3" size={20} />
                  <span className="w-20 text-sm">Desktop</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 mx-3">
                    <div className="bg-blue-600 h-4 rounded-full" style={{ width: `${getDevicePercent('desktop')}%` }}></div>
                  </div>
                  <span className="text-sm font-bold">{getDevicePercent('desktop')}%</span>
                </div>
                <div className="flex items-center">
                  <Smartphone className="text-blue-600 mr-3" size={20} />
                  <span className="w-20 text-sm">Mobile</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 mx-3">
                    <div className="bg-green-500 h-4 rounded-full" style={{ width: `${getDevicePercent('mobile')}%` }}></div>
                  </div>
                  <span className="text-sm font-bold">{getDevicePercent('mobile')}%</span>
                </div>
                <div className="flex items-center">
                  <Tablet className="text-purple-600 mr-3" size={20} />
                  <span className="w-20 text-sm">Tablet</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 mx-3">
                    <div className="bg-purple-500 h-4 rounded-full" style={{ width: `${getDevicePercent('tablet')}%` }}></div>
                  </div>
                  <span className="text-sm font-bold">{getDevicePercent('tablet')}%</span>
                </div>
              </div>
            )}
          </div>

          {/* Traffic Sources */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Share2 className="mr-2 text-green-600" size={24} />
              Origine Traffico
            </h2>
            {loading ? (
              <div className="space-y-3 animate-pulse">
                {[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-gray-200 rounded"></div>)}
              </div>
            ) : trafficSources.length > 0 ? (
              <div className="space-y-3">
                {trafficSources.slice(0, 8).map((source, i) => (
                  <div key={i} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div className="flex items-center">
                      <Globe size={16} className="text-gray-400 mr-2" />
                      <span className="text-sm truncate max-w-[200px]">{source.source || 'Diretto'}</span>
                    </div>
                    <span className="text-sm font-bold text-green-600">{source.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessun dato disponibile</p>
            )}
          </div>

          {/* Countries */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <MapPin className="mr-2 text-red-600" size={24} />
              Paesi Visitatori
            </h2>
            {loading ? (
              <div className="space-y-3 animate-pulse">
                {[1,2,3,4,5].map(i => <div key={i} className="h-8 bg-gray-200 rounded"></div>)}
              </div>
            ) : countries.length > 0 ? (
              <div className="space-y-3">
                {countries.slice(0, 8).map((country, i) => (
                  <div key={i} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <span className="text-sm">{country.country || 'Sconosciuto'}</span>
                    <span className="text-sm font-bold text-red-600">{country.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessun dato disponibile</p>
            )}
          </div>
        </div>

        {/* Bottom Grid - Funnel & Preferences */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Conversion Funnel */}
          <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <TrendingUp className="mr-2 text-purple-600" size={24} />
              Funnel di Conversione per Evento
            </h2>
            {loading ? (
              <div className="space-y-3 animate-pulse">
                {[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-200 rounded"></div>)}
              </div>
            ) : funnelData.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Evento</th>
                      <th className="text-center py-2 px-2">Visite</th>
                      <th className="text-center py-2 px-2">Form Aperto</th>
                      <th className="text-center py-2 px-2">Inviato</th>
                      <th className="text-center py-2 px-2">Completato</th>
                      <th className="text-right py-2 px-2">Conv. %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnelData.slice(0, 10).map((item, i) => (
                      <tr key={i} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-2 truncate max-w-[150px]">{item.event_title}</td>
                        <td className="text-center py-2 px-2">{item.steps?.view || 0}</td>
                        <td className="text-center py-2 px-2">{item.steps?.form_open || 0}</td>
                        <td className="text-center py-2 px-2">{item.steps?.form_submit || 0}</td>
                        <td className="text-center py-2 px-2">{item.steps?.registration_complete || 0}</td>
                        <td className="text-right py-2 px-2 font-bold text-purple-600">{item.conversion_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Nessun dato funnel disponibile</p>
            )}
          </div>

          {/* User Preferences */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Bike className="mr-2 text-orange-600" size={24} />
              Preferenze Utenti
            </h2>
            {loading ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-24 bg-gray-200 rounded"></div>
                <div className="h-24 bg-gray-200 rounded"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Motorcycle Choices */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">Scelta Moto</h3>
                  {motoChoices.length > 0 ? (
                    <div className="space-y-2">
                      {motoChoices.slice(0, 5).map((moto, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="truncate max-w-[150px]">{moto.choice}</span>
                          <span className="font-bold text-orange-600">{moto.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">Nessun dato</p>
                  )}
                </div>
                
                {/* Room Types */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2 flex items-center">
                    <BedDouble size={16} className="mr-1" />
                    Tipo Camera
                  </h3>
                  {roomTypes.length > 0 ? (
                    <div className="space-y-2">
                      {roomTypes.map((room, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{room.type === 'single' ? 'Singola' : room.type === 'double' ? 'Doppia' : room.type}</span>
                          <span className="font-bold text-blue-600">{room.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">Nessun dato</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Browsers */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <Chrome className="mr-2 text-yellow-600" size={24} />
            Browser Utilizzati
          </h2>
          {loading ? (
            <div className="flex gap-4 animate-pulse">
              {[1,2,3,4].map(i => <div key={i} className="h-20 w-32 bg-gray-200 rounded"></div>)}
            </div>
          ) : browsers.length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {browsers.slice(0, 6).map((browser, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4 text-center min-w-[100px]">
                  <p className="text-2xl font-bold text-gray-900">{browser.count}</p>
                  <p className="text-sm text-gray-500">{browser.browser || 'Altro'}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">Nessun dato browser disponibile</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
